import { describeError, dirhams, monthYear, whatsappLink } from '@horof/shared';
import type { CombinedError } from 'urql';

export type Receipt = {
  id: string;
  receiptNo: string;
  amount: string;
  receiptLink: string;
  billMonth: string | null;
  paymentDate?: string;
};

/**
 * A WhatsApp link (wa.me) to the member's notify phone, with the receipts.
 * The collector's own WhatsApp opens with the message ready to send.
 */
export function whatsappReceiptsLink(
  phone: string,
  receipts: Receipt[],
  associationName: string,
): string {
  const lines = [
    'السلام عليكم،',
    `توصّلت ${associationName} منكم بما يلي:`,
    ...receipts.map(
      (r) =>
        `• ${dirhams(r.amount)}${r.billMonth ? ` (فاتورة ${monthYear(r.billMonth)})` : ''}، وصل رقم ${r.receiptNo}:\n${r.receiptLink}`,
    ),
    'شكراً لكم.',
  ];
  return whatsappLink(phone, lines.join('\n'));
}

/** The API's payment errors, in words the collector can act on. */
export function paymentErrorMessage(error: CombinedError | undefined): string {
  if (error?.networkError) {
    // The payment may have been saved before the connection dropped: the page
    // refetches the bills, and the collector checks them before trying again.
    return 'انقطع الاتصال. تحقّق من الفواتير أدناه (قد يكون الأداء سُجِّل) قبل إعادة المحاولة.';
  }
  return describeError(
    error,
    'تعذّر تسجيل الأداء. حدّث الصفحة وتحقّق من الفواتير.',
    PAYMENT_ERRORS,
  );
}

const PAYMENT_ERRORS = {
  BILL_ALREADY_PAID: 'هذه الفاتورة مؤداة من قبل.',
  AMOUNT_NOT_POSITIVE: 'المبلغ غير صحيح.',
  BILL_NOT_FOUND: 'هذه الفاتورة لم تعد موجودة. حدّث الصفحة.',
};

/**
 * A failed bill in a bulk payment. `appBulkSavePayments` reports each one as
 * data (its English message, not a code), so only the messages are matched.
 */
export function bulkFailureMessage(message: string | null): string {
  if (message && /already paid/i.test(message)) {
    return PAYMENT_ERRORS.BILL_ALREADY_PAID;
  }
  if (message && /greater than 0/i.test(message)) {
    return PAYMENT_ERRORS.AMOUNT_NOT_POSITIVE;
  }
  return `تعذّر أداء فاتورة${message ? ` (${message})` : ''}.`;
}
